import { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import ExportDialog from "components/ExportDialog";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  handleExport,
  customTitle,
  isAdmin,
  totalCount,
  pageCount,
  onPageChange,
  onPageSizeChange,
  currentPage,
  pageSize,
}) {
  // const defaultValue =
  //   typeof entriesPerPage === "object" && entriesPerPage?.defaultValue
  //     ? entriesPerPage.defaultValue
  //     : 10;

  const entries =
    typeof entriesPerPage === "object" && entriesPerPage?.entries
      ? entriesPerPage.entries.map((el) => el.toString())
      : ["5", "10", "15", "20", "25"];

  const columns = useMemo(() => table.columns, [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);

  const initialState = useMemo(() => {
    const defaultSortColumn = columns.find(
      (column) => column.accessor === "Lancamento" || column.accessor === "lancamento"
    )?.accessor;

    if (!defaultSortColumn) {
      return {};
    }

    return {
      sortBy: [{
        id: defaultSortColumn,
        desc: true,
      }],
    };
  }, [columns]);

  const tableInstance = useTable({ 
      columns, 
      data, 
      initialState, 
      manualPagination: true,
      pageCount: pageCount,
      state: {
        pageIndex: currentPage - 1,
        pageSize: pageSize
      }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // useEffect(() => {
  //   if (onPageChange) {
  //     onPageChange(pageIndex, pageSize);
  //   }
  // }, [pageIndex, pageSize]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setGlobalFilter,
    state: { pageIndex, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  //useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  //const setEntriesPerPage = (value) => setPageSize(value);

  const renderPagination = Array.from({ length: pageCount }, (_, i) => (
    <MDPagination
      item
      key={i}
      onClick={() => onPageChange(i + 1)}
      active={currentPage === i + 1}
    >
      {i + 1}
    </MDPagination>
  ));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;
    
    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asc";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }
    
    return sortedValue;
  };

  const entriesStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const entriesEnd = Math.min(currentPage * pageSize, totalCount);

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  const newSize = parseInt(newValue, 10);
                  onPageSizeChange(newSize);
                  onPageChange(1);
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;resultados por página
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Pesquisar..."
                value={search}
                size="small"
                fullWidth
                onChange={({ target: { value } }) => {
                  setSearch(value);
                  onSearchChange(value); 
                }}
              />
            </MDBox>
          )}
          {isAdmin && (
              /* Botão exportar com filtro */
              <MDBox display="flex" justifyContent="flex-end" mb={2} px={2}>
                <ExportDialog title={customTitle} onExport={handleExport} />
              </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()} sx={{ borderCollapse: "collapse", borderSpacing: 1 }}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                  sx={{ padding: 0, margin: 0 }}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow key={key} {...row.getRowProps()}>
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                    sx={{ padding: 0, margin: 0 }}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageCount === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Mostrando {entriesStart} a {entriesEnd} de {totalCount} resultados
            </MDTypography>
          </MDBox>
        )}
        {pageCount > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {currentPage > 1 && (
              <MDPagination item onClick={() => onPageChange(currentPage - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: pageCount }}
                  value={currentPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 1 && value <= pageCount) {
                      onPageChange(value);
                    }
                  }}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {currentPage < pageCount && (
              <MDPagination item onClick={() => onPageChange(currentPage + 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,

  totalCount: PropTypes.number,
  pageCount: PropTypes.number,
  onPageChange: PropTypes.func,
};

export default DataTable;
